import {first} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {
  emptyCleanHtml,
  iSolemnlySwearThisHtmlIsClean,
} from '../../../libs/shared/core/src/helpers/typescript/cleanHtml';
import {LoadedMessageModel} from '../../../libs/shared/core/src/models/api/messageModel';
import {ViewerPanelId} from '../../../libs/shared/core/src/store/panels/panelStateBase';
import {TimelineVariationsEnum} from '../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {VisualRecipientMap} from '../../helpers/models/recipientHelpers';
import {MessageViewerAvatar} from '../inboxes/viewer/activity/message/messageViewerAvatar';
import {MessageViewerBodyAiHarvesting} from '../inboxes/viewer/activity/message/messageViewerBodyAiHarvesting';
import {MessageViewerDetails} from '../inboxes/viewer/activity/message/messageViewerDetails';

/*
 * Constants.
 */

export enum AiHarvestingMessageViewHeaderVariantsEnum {
  SUBJECT = 'subject',
  SENDER = 'sender',
}

/*
 * Props.
 */

export interface AiHarvestingMessageViewProps {
  panelId?: ViewerPanelId;
  message: LoadedMessageModel;
  variant?: AiHarvestingMessageViewHeaderVariantsEnum;
  recipients?: VisualRecipientMap;
}

/*
 * Styles.
 */

const StyledContainerDiv = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const StyledWrapperDiv = styled.div`
  border-radius: 16px;
  box-shadow: 0px 1px 9px 0px ${(p) => p.theme.alphas.black20};
  border: 1px solid ${(p) => p.theme.alphas.black20};
  display: flex;
  flex-direction: column;
`;

const StyledSubjectContainerDiv = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  padding: 14px;

  border-bottom: 1px solid ${(p) => p.theme.alphas.black30};
`;

const StyledSenderDetailsWrapperDiv = styled(StyledSubjectContainerDiv)`
  padding-left: 8px;
`;

const StyledSubjectPrefixDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledSubjectDiv = styled.div`
  color: ${(p) => p.theme.greys.shade80};
`;

const StyledBodyDiv = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.normal};
  font-size: ${fontSizes.medium};

  padding: 16px;
  line-height: 20px;
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: 0;
`;

const StyledHeaderDetailsDiv = styled.div`
  grid-area: details;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

/*
 * Component.
 */

export const AiHarvestingMessageView: FC<AiHarvestingMessageViewProps> = ({
  panelId,
  message,
  variant = AiHarvestingMessageViewHeaderVariantsEnum.SUBJECT,
  recipients,
}) => {
  const renderHeader = () => {
    if (variant === AiHarvestingMessageViewHeaderVariantsEnum.SENDER && recipients && panelId) {
      return (
        <StyledSenderDetailsWrapperDiv>
          <MessageViewerAvatar panelId={panelId} message={message} author={first(recipients.from)} />
          <StyledHeaderDetailsDiv>
            <MessageViewerDetails
              panelId={panelId}
              message={message}
              visualRecipients={recipients}
              activitySourceVisualType={undefined}
              isMessageViewerExpanded
              shouldHideSubject
              shouldShowSubjectWhenCollapsed
              hasMessageAnnotations={false}
            />
          </StyledHeaderDetailsDiv>
        </StyledSenderDetailsWrapperDiv>
      );
    }
    return (
      <StyledSubjectContainerDiv>
        <StyledSubjectPrefixDiv>
          <Trans id="ai-harvesting-message-viewer-subject">Subject</Trans>:&nbsp;
        </StyledSubjectPrefixDiv>
        <StyledSubjectDiv>{message.subject}</StyledSubjectDiv>
      </StyledSubjectContainerDiv>
    );
  };

  return (
    <StyledContainerDiv>
      <StyledWrapperDiv>
        {renderHeader()}
        <StyledBodyDiv>
          <MessageViewerBodyAiHarvesting
            messageId={message.id}
            trustedHtml={iSolemnlySwearThisHtmlIsClean(message.lightBody || message.body || emptyCleanHtml)}
            isExternalContentHidden
            attachments={[]}
            timelineVariation={TimelineVariationsEnum.ASYNCHRONOUS}
            isInbound={message.isInbound}
          />
        </StyledBodyDiv>
      </StyledWrapperDiv>
    </StyledContainerDiv>
  );
};
